import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import useReferralStats from '../../hooks/useReferralStats';
import './ReferralInviteModal.css';

const COPIED_FEEDBACK_MS = 2000;
const MONTHLY_CAP = 5;

/**
 * Modal where the user can view / copy / share their referral invite link
 * and see basic referral stats.
 *
 * Follows the PointsBreakdownModal pattern (overlay click + Escape to close).
 */
const ReferralInviteModal = ({ onClose }) => {
  const { user, profile } = useProfile();
  const { monthlyCount, totalCount, loading: statsLoading } = useReferralStats(
    user?.id,
  );
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef(null);

  // referral_code lives on the profiles table row, not the auth user object
  const referralCode = profile?.referral_code || '';
  const referralLink = referralCode
    ? `${window.location.origin}/invite.html?ref=${referralCode}`
    : '';

  const canShare =
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function';

  /* ---------- Escape key ---------- */
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  /* ---------- Cleanup copy timer ---------- */
  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  /* ---------- Copy ---------- */
  const handleCopy = useCallback(async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => {
        setCopied(false);
        copiedTimerRef.current = null;
      }, COPIED_FEEDBACK_MS);
    } catch {
      // Fallback: the input is selectable so the user can copy manually
    }
  }, [referralLink]);

  /* ---------- Share (Web Share API) ---------- */
  const handleShare = useCallback(async () => {
    if (!referralLink || !canShare) return;
    try {
      await navigator.share({
        title: 'Join Zoomed In!',
        text: 'Sign up with my invite link and get 150 bonus coins!',
        url: referralLink,
      });
    } catch {
      // User cancelled or share failed — no action needed
    }
  }, [referralLink, canShare]);

  return (
    <div className="referral-modal-overlay" onClick={onClose}>
      <div
        className="referral-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Invite Friends"
      >
        <button
          className="referral-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>

        <h2 className="referral-modal-title">Invite Friends</h2>
        <p className="referral-modal-subtitle">
          Share your invite link &mdash; you earn{' '}
          <strong className="referral-modal-reward">100 coins per sign-up</strong> and they get{' '}
          <strong className="referral-modal-reward">150 bonus coins</strong> when they sign up.{' '}
          Max {MONTHLY_CAP} sign-ups per month.
        </p>

        {/* ---- Link + Copy ---- */}
        <div className="referral-modal-link-group">
          <input
            className="referral-modal-link-input"
            type="text"
            value={referralLink}
            readOnly
            onFocus={(e) => e.target.select()}
            aria-label="Referral link"
          />
          <button
            className={`referral-modal-copy-btn${copied ? ' is-copied' : ''}`}
            onClick={handleCopy}
            disabled={!referralLink}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        {/* ---- Share (mobile) ---- */}
        {canShare && (
          <button
            className="referral-modal-share-btn"
            onClick={handleShare}
            disabled={!referralLink}
          >
            Share Invite Link
          </button>
        )}

        {/* ---- Referral stats ---- */}
        <div className="referral-modal-stats">
          <h3>Your Referrals</h3>
          {statsLoading ? (
            <p className="referral-modal-stats-loading">Loading...</p>
          ) : (
            <div className="referral-modal-stats-row">
              <div className="referral-modal-stat">
                <span className="referral-modal-stat-value">
                  {monthlyCount}
                </span>
                <span className="referral-modal-stat-label">
                  This month ({monthlyCount} / {MONTHLY_CAP})
                </span>
              </div>
              <div className="referral-modal-stat">
                <span className="referral-modal-stat-value">{totalCount}</span>
                <span className="referral-modal-stat-label">All time</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralInviteModal;
